import { useCallback, useMemo, useState } from 'react'
import { MonitorX, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchDeviceBlocks,
  createDeviceBlock,
  removeDeviceBlock,
  BAN_STR as S,
  type DeviceBlockRow,
} from './strikesApi'

type Kind = DeviceBlockRow['kind']
const KINDS: { value: Kind; label: string }[] = [
  { value: 'ip', label: S.kindIp },
  { value: 'device', label: S.kindDevice },
  { value: 'subnet', label: S.kindSubnet },
]

const inputCls =
  'rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong'

/**
 * A8.16 — IP/qurilma bloklari. JONLI `/admin/ip-blocks` (000245): IP/podset/
 * qurilma-otpechatoklari qora ro'yxati — inline qo'shish ((kind,value) bo'yicha
 * idempotent) + o'chirish.
 */
export function DeviceBlocksScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [kind, setKind] = useState<Kind>('ip')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const add = async () => {
    if (!value.trim()) return
    setBusy(true)
    try {
      await createDeviceBlock({ kind, value: value.trim(), reason: reason.trim() })
      toast(S.addOk, { tone: 'success' })
      setValue('')
      setReason('')
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<AdminColumn<DeviceBlockRow>[]>(
    () => [
      {
        key: 'kind',
        header: S.blockKind,
        type: 'enum',
        width: 120,
        options: [
          { value: 'ip', label: S.kindIp, status: 'review' },
          { value: 'device', label: S.kindDevice, status: 'pending' },
          { value: 'subnet', label: S.kindSubnet, status: 'banned' },
        ],
      },
      { key: 'value', header: S.value, type: 'text', minWidth: 200 },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 180 },
      { key: 'hits', header: S.hits, type: 'number', width: 110, align: 'right' },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<DeviceBlockRow>[]>(
    () => [
      {
        id: 'remove',
        label: S.remove,
        icon: <Trash2 size={14} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: async (r) => {
          try {
            await removeDeviceBlock(r.id)
            toast(S.removed, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchDeviceBlocks, [])
  return (
    <>
      <PageHeader title={S.deviceBlocks} screenId="A8.16" description={S.deviceBlocksNote} />
      <div className="flex flex-wrap items-end gap-2 p-4 pb-0">
        <select value={kind} onChange={(e) => setKind(e.target.value as Kind)} className={inputCls}>
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={S.value}
          spellCheck={false}
          className={`${inputCls} min-w-[200px] flex-1 font-mono`}
        />
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={S.reason}
          className={`${inputCls} min-w-[160px] flex-1`}
        />
        <button
          type="button"
          onClick={add}
          disabled={busy || !value.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {S.add}
        </button>
      </div>
      <DataTable<DeviceBlockRow>
        key={reloadKey}
        screenId="A8.16"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <MonitorX size={28} strokeWidth={1.5} />, title: S.deviceBlocks }}
      />
    </>
  )
}
